import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Image as ImageIcon } from "lucide-react";
import PageShell from "@/components/PageShell";
import { getApiUrl } from "@/utils/api";
import { resolveImageUrl } from "@/utils/imageUrl";
import { useBasket } from "@/context/BasketContext";

const API = getApiUrl();

/**
 * Payment confirmation. Polls /api/checkout/status until paid/failed/expired
 * or up to 15 attempts (30 seconds). Clears the basket on `paid`.
 */
export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");   // polling | paid | failed | expired | not_found
  const [order, setOrder] = useState(null);
  const basket = useBasket();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("not_found"); return; }
    let attempts = 0;
    let timer = null;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setOrder(data.order);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) { basket.clear(); clearedRef.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus(data.payment_status); return; }
        if (attempts >= 15) { setStatus("timeout"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts >= 15) { setStatus("not_found"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <PageShell chrome>
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="payment-success-page">
        {status === "polling" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-serif">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">Please don&apos;t close this window.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-3xl font-serif mb-2" data-testid="payment-success-title">Thank you!</h1>
            <p className="text-muted-foreground mb-6">Your order has been received.</p>
            {order && (
              <div className="text-left border rounded p-4 bg-background inline-block min-w-[280px]">
                <div className="text-xs text-muted-foreground mb-2">Order #{order.order_id?.slice(0, 8)}</div>
                {(order.items || []).map((it, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-1.5" data-testid={`receipt-item-${idx}`}>
                    <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-muted flex items-center justify-center">
                      {it.image_url ? (
                        <img
                          src={resolveImageUrl(it.image_url)}
                          alt={it.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          data-testid={`receipt-item-thumb-${idx}`}
                        />
                      ) : (
                        <ImageIcon className="h-4 w-4 text-muted-foreground/50" />
                      )}
                    </div>
                    <span className="flex-1 text-sm truncate">{it.name}</span>
                    <span className="text-sm whitespace-nowrap">{order.currency} {Number(it.price).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t mt-2 pt-2 flex justify-between font-serif">
                  <span>Total</span>
                  <span>{order.currency} {Number(order.subtotal || 0).toFixed(2)}</span>
                </div>
              </div>
            )}
            <div className="mt-8">
              <Link to="/" className="text-primary underline text-sm">Continue shopping</Link>
            </div>
          </>
        )}
        {["failed", "expired", "timeout", "not_found"].includes(status) && (
          <>
            <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-serif mb-2">
              {status === "failed" && "Payment failed"}
              {status === "expired" && "Checkout expired"}
              {status === "timeout" && "Confirmation is taking longer than usual"}
              {status === "not_found" && "We couldn't find that order"}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {status === "timeout"
                ? "Refresh in a moment or check your email — we'll send a receipt once payment clears."
                : "You can try again from your basket."}
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/basket" data-testid="fail-back-to-basket" className="text-primary underline text-sm">Back to basket</Link>
              <Link to="/" className="text-muted-foreground underline text-sm">Back to shop</Link>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
}
